<script lang="ts">
  // Phone account sheet.
  //
  // The topbar used to lay out identity + language + theme + Manage + Sign out
  // in one flex row. On a phone that wrapped onto three lines and ate a third
  // of the screen before any content appeared. Here the topbar keeps one button
  // and everything behind it lives in a bottom sheet with real tap targets.
  import { PALETTES, logout, session, setTheme, theme, type Theme } from "../lib/stores";
  import { LOCALES, locale, setLocale, t } from "../lib/i18n";
  import { focusTrap } from "../lib/focusTrap";

  let { onClose, onManage }: { onClose: () => void; onManage: () => void } = $props();

  const choices: Theme[] = ["auto", ...PALETTES.map((p) => p.name)];
  const swatchFor = (choice: Theme) =>
    PALETTES.find((p) => p.name === choice)?.swatch ?? "var(--chip)";
</script>

<svelte:window onkeydown={(e) => e.key === "Escape" && onClose()} />

<div class="backdrop" role="presentation" onclick={(e) => e.target === e.currentTarget && onClose()}>
  <div
    class="sheet panel"
    role="dialog"
    aria-label={$t("account.title")}
    aria-modal="true"
    tabindex="-1"
    use:focusTrap
  >
    <span class="grabber" aria-hidden="true"></span>

    <header>
      {#if $session?.picture}
        <img class="avatar" src={$session.picture} alt="" referrerpolicy="no-referrer" />
      {/if}
      <span class="who">
        <strong>{$session?.name}</strong>
        <span class="sub">
          {$session?.role === "admin" ? $t("admin.roleAdmin") : $t("admin.roleMember")}
        </span>
      </span>
      <button class="x" onclick={onClose} aria-label={$t("day.close")}>✕</button>
    </header>

    <section class="group">
      <span class="lab">{$t("lang.choose")}</span>
      <div class="seg">
        {#each LOCALES as l (l.code)}
          <button
            class="seg-btn"
            class:on={$locale === l.code}
            aria-pressed={$locale === l.code}
            onclick={() => setLocale(l.code)}
          >
            <span class="short">{l.short}</span>
            {l.label}
          </button>
        {/each}
      </div>
    </section>

    <section class="group">
      <span class="lab">{$t("topbar.theme")}</span>
      <div class="themes">
        {#each choices as choice (choice)}
          <button
            class="theme-btn"
            class:on={$theme === choice}
            aria-pressed={$theme === choice}
            onclick={() => setTheme(choice)}
          >
            <span
              class="swatch"
              class:auto={choice === "auto"}
              style="background: {swatchFor(choice)}"
            ></span>
            {$t(`theme.${choice}`)}
          </button>
        {/each}
      </div>
    </section>

    <div class="actions">
      {#if $session?.role === "admin"}
        <button class="btn" onclick={onManage}>{$t("admin.title")}</button>
      {/if}
      <button class="btn secondary" onclick={() => logout()}>
        {$t("topbar.signout")}
      </button>
    </div>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    display: grid;
    place-items: end center;
    background: var(--backdrop);
    backdrop-filter: blur(3px);
  }
  .sheet {
    width: 100%;
    max-height: 92svh;
    overflow: auto;
    overscroll-behavior: contain;
    display: grid;
    gap: 18px;
    border-radius: 22px 22px 0 0;
    padding: 10px 16px calc(18px + var(--safe-b));
  }
  /* the universal "this drags down / is dismissible" affordance */
  .grabber {
    justify-self: center;
    width: 44px;
    height: 4px;
    border-radius: 999px;
    background: var(--line);
  }

  header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    flex: 0 0 auto;
  }
  .who {
    flex: 1;
    min-width: 0;
    display: grid;
  }
  .who strong {
    font-size: 17px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who .sub {
    color: var(--muted);
    font-size: 13px;
    font-weight: 700;
  }
  .x {
    flex: 0 0 auto;
    width: var(--tap);
    height: var(--tap);
    border: 0;
    border-radius: 12px;
    background: var(--chip);
    color: var(--ink);
    font-size: 16px;
  }

  .group {
    display: grid;
    gap: 8px;
  }
  .lab {
    color: var(--muted);
    font-size: 12.5px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .seg {
    display: flex;
    gap: 6px;
    padding: 5px;
    border: 1px solid var(--line);
    border-radius: 15px;
    background: var(--surface);
  }
  .seg-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: var(--tap);
    border: 0;
    border-radius: 11px;
    background: transparent;
    color: var(--muted);
    font-size: 15px;
    font-weight: 800;
  }
  .seg-btn.on {
    background: var(--btn);
    color: var(--btn-fg);
  }
  .seg-btn .short {
    display: inline-grid;
    place-items: center;
    min-width: 28px;
    height: 22px;
    border-radius: 7px;
    background: color-mix(in srgb, currentColor 16%, transparent);
    font-size: 12px;
    font-weight: 900;
  }

  /* two per row: the palette names ("Midnight", "Automatisch (System)") are far
     too long for a single-row segmented control */
  .themes {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .theme-btn {
    display: flex;
    align-items: center;
    gap: 9px;
    min-height: var(--tap);
    padding: 0 12px;
    border: 1px solid var(--line);
    border-radius: 13px;
    background: var(--surface);
    color: var(--ink);
    font-size: 14px;
    font-weight: 700;
    text-align: left;
    min-width: 0;
  }
  .theme-btn.on {
    border-color: var(--yes);
    box-shadow: inset 0 0 0 1px var(--yes);
  }
  .theme-btn > :global(*) {
    min-width: 0;
  }
  .swatch {
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    border: 1px solid var(--line);
    border-radius: 8px;
  }
  /* "auto" has no palette of its own — show it split light/dark */
  .swatch.auto {
    background: linear-gradient(135deg, #f6f1e8 0 50%, #141715 50% 100%) !important;
  }

  .actions {
    display: grid;
    gap: 8px;
  }
  .actions .btn {
    min-height: 48px;
  }
</style>
